// ─────────────────────────────────────────────────────────────
// RawExpressionModal — universal fallback editor for a MethodDef's FormalExpression.
//
// Some production formulas (hand-written JS IIFEs, hybrids, empty-guard deletions) can't be
// represented in the visual builder. This editor lets the author view/edit the raw OpenEDC
// expression text directly and save it back verbatim into the MethodDef — so EVERY formula is
// editable, not just the builder-grammar ones.
// ─────────────────────────────────────────────────────────────

import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack,
  Typography, TextField, Chip,
} from '@mui/material';
import FunctionsIcon from '@mui/icons-material/Functions';

export interface RawExpressionModalProps {
  open: boolean;
  onClose: () => void;
  /** Saves the edited expression text for the given MethodDef. */
  onSave: (methodOID: string, text: string) => void;
  methodOID: string | null;
  /** Display label (derived item name / MetaKey). */
  label?: string;
  /** Current expression text. */
  initialText: string;
}

export function RawExpressionModal(props: RawExpressionModalProps): JSX.Element {
  const { open, onClose, onSave, methodOID, label, initialText } = props;
  const [text, setText] = useState<string>(initialText);

  useEffect(() => {
    if (open) setText(initialText);
  }, [open, initialText]);

  const changed = text.trim() !== initialText.trim();
  const canSave = !!methodOID && text.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <FunctionsIcon color="secondary" />
          <Box>
            <Typography variant="h6">Edit expression</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {label ? <>Derived: <b>{label}</b>{methodOID ? <> &middot; {methodOID}</> : null}</> : (methodOID ?? '')}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Chip size="small" label="OpenEDC" variant="outlined" />
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          Raw FormalExpression — edited and saved verbatim into the MethodDef. Use this for formulas
          the visual builder can&rsquo;t represent (JavaScript IIFEs, hybrids, empty-guard deletions).
          Take care: an invalid expression will not evaluate.
        </Typography>
        <TextField
          fullWidth multiline minRows={6} maxRows={20}
          value={text}
          onChange={e => setText(e.target.value)}
          spellCheck={false}
          inputProps={{ style: { fontFamily: 'JetBrains Mono, monospace', fontSize: 12.5, lineHeight: 1.6, whiteSpace: 'pre' } }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSave || !changed}
          onClick={() => { if (methodOID) onSave(methodOID, text); }}
        >
          Save expression
        </Button>
      </DialogActions>
    </Dialog>
  );
}
